"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { respondRfiAction, uploadDocumentAction } from "@/app/actions";

export default function RfiResponder({
  rfiId,
  customerId,
  actingEmail,
}: {
  rfiId: string;
  customerId: string;
  actingEmail?: string | null;
}) {
  const router = useRouter();
  const [pending, start] = useTransition();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [docIds, setDocIds] = useState<Array<{ id: string; name: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onUpload(files: FileList | null) {
    if (!files) return;
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.set("file", file);
      fd.set("purpose", "rfi_response");
      fd.set("name", file.name);
      const res = await uploadDocumentAction(fd);
      if (res.ok) setDocIds((d) => [...d, { id: res.data.id, name: file.name }]);
      else setError(`${file.name}: ${res.error}`);
    }
  }

  function submit() {
    setError(null);
    start(async () => {
      const res = await respondRfiAction(rfiId, customerId, {
        message,
        ...(docIds.length ? { documentIds: docIds.map((d) => d.id) } : {}),
      });
      if (res.ok) {
        setDone(true);
        setOpen(false);
        router.refresh();
      } else setError(res.error);
    });
  }

  if (done) return <span className="pill pill-green">response sent</span>;

  return (
    <div>
      {!open ? (
        <button className="btn btn-dark !px-4 !py-1.5 text-[13px]" onClick={() => setOpen(true)}>
          Respond
        </button>
      ) : (
        <div className="card-cream mt-3 p-4">
          {actingEmail ? (
            <p className="mb-2 text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
              Responding as {actingEmail}
            </p>
          ) : (
            <p className="mb-2 text-[12px] font-light" style={{ color: "var(--c-danger)" }}>
              Select who you're acting as (top-left) before sending — Conduit records who answered.
            </p>
          )}
          <textarea
            className="field min-h-[80px]"
            placeholder="Your response…"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <div className="mt-2 flex items-center gap-3">
            <label className="btn btn-ghost !px-4 !py-1.5 cursor-pointer text-[13px]">
              Attach files
              <input type="file" multiple hidden onChange={(e) => onUpload(e.target.files)} />
            </label>
            {docIds.map((d) => (
              <span key={d.id} className="pill pill-outline">
                {d.name}
              </span>
            ))}
            <button
              className="btn btn-primary !px-4 !py-1.5 ml-auto text-[13px]"
              disabled={pending || !message || !actingEmail}
              onClick={submit}
            >
              {pending ? "Sending…" : "Send response"}
            </button>
          </div>
          {error && (
            <p className="mt-2 text-[13px] font-light" style={{ color: "var(--c-danger)" }}>
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
